import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Pagination } from '../../../model/paginatiion';
import { IAgreementResponse } from '../../../model/base/response/agreement-response';

interface AgreementsState {
  agreements: Pagination<IAgreementResponse>;
  loading: boolean;
  error: string | null;
}

type AgreementsAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: Pagination<IAgreementResponse> }
  | { type: 'LOAD_ERROR'; payload: string };

const initialState: AgreementsState = {
  agreements: new Pagination<IAgreementResponse>([], 1, 0, 10, 0),
  loading: false,
  error: null,
};

const AgreementsStateContext = createContext<AgreementsState | undefined>(undefined);
const AgreementsDispatchContext = createContext<React.Dispatch<AgreementsAction> | undefined>(undefined);

function agreementsReducer(state: AgreementsState, action: AgreementsAction): AgreementsState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: null };
    case 'LOAD_SUCCESS':
      return { ...state, loading: false, agreements: action.payload };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

export function AgreementsProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(agreementsReducer, initialState);

  return (
    <AgreementsStateContext.Provider value={state}>
      <AgreementsDispatchContext.Provider value={dispatch}>
        {children}
      </AgreementsDispatchContext.Provider>
    </AgreementsStateContext.Provider>
  );
}

export function useAgreementsState() {
  const context = useContext(AgreementsStateContext);
  if (context === undefined) {
    throw new Error('useAgreementsState must be used within AgreementsProvider');
  }
  return context;
}

export function useAgreementsDispatch() {
  const context = useContext(AgreementsDispatchContext);
  if (context === undefined) {
    throw new Error('useAgreementsDispatch must be used within AgreementsProvider');
  }
  return context;
}
